/**
 * COM-7.3 — approving a pay period.
 *
 * ⚠️ THIS FILE DECIDES NOTHING THE DATABASE DOES NOT ALSO DECIDE.
 * `commission_approve_period()` is SECURITY DEFINER and checks
 * `commission.approve` itself. What lives here is the same shape, so a screen
 * can preview before it commits and explain what it is about to do. Same
 * division as `commissionBulkAssign.ts`, whose preview-then-commit pattern this
 * follows deliberately.
 *
 * Everything below was measured against the live function on 2026-08-26, before
 * any of it was written. Three of the findings changed the design:
 *
 * ⚠️ 1. AN EMPTY PREVIEW DOES NOT MEAN NOTHING WILL HAPPEN.
 *    A whole-batch approve of a period with no approvable lines returns ZERO
 *    ROWS from the dry run, and the commit still moves the period to
 *    `approved` and stamps `approved_at`. Measured on an empty period: dry run
 *    0 rows, commit -> state `approved`.
 *    So "nothing to approve" and "nothing will happen" are different
 *    statements, and a confirm dialog that renders an empty preview as the
 *    latter is lying. `describeApproval()` owns that sentence, and
 *    `closesTheBatch()` is what tells a caller the period itself is about to
 *    change even when no line is.
 *
 * ⚠️ 2. AN APPROVED PERIOD IS NOT A PERIOD WHOSE LINES ARE ALL APPROVED.
 *    Held lines are SKIPPED, not approved. Measured on the live August period:
 *    8 pending lines, 7 held; approving gave `approved_lines=1 skipped=7`, the
 *    period went to `approved`, and the 7 held lines stayed `pending`. This is
 *    COM-7.2's rule arriving at its verb -- held is exactly what stands between
 *    approved and paid -- and a screen that reports "period approved" without
 *    the skipped count has told an operator their held money is cleared.
 *
 * ⚠️ 3. APPROVING NAMED PEOPLE AND CLOSING THE BATCH ARE MUTUALLY EXCLUSIVE.
 *    The database refuses `p_close_batch => true` alongside a subject list,
 *    because it would leave unapproved lines inside a closed batch. So the two
 *    controls are ONE choice, not two checkboxes: approve everyone and close
 *    the period, or approve named people and leave it open. `ApprovalScope`
 *    models that as a union so the impossible pair cannot be constructed.
 *
 * ⚠️ APPROVING IS NOT PAYING. Different permission, different story (COM-7.5),
 * and deliberately different authorities so one person cannot move money end to
 * end. Nothing in this file may grow a pay path.
 */

import { type Decimal, formatMoneyDisplay, tryDecimal } from './money';

// ─────────────────────────────────────────────────────────────────────────────
// Rows
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ Must equal the `outcome` values `commission_approve_period()` emits, and
 * it is an OPEN SET: read it as a hint, never as exhaustive. Extracted from
 * `prosrc` on 2026-08-26 rather than from the rows a probe happened to trigger
 * -- two of these four never appeared in any probe.
 */
export const APPROVAL_OUTCOMES = [
  'would_approve',
  'approved',
  'no_lines',
  'nothing_pending',
] as const;
export type ApprovalOutcome = (typeof APPROVAL_OUTCOMES)[number];

/** One row of `commission_approve_period()`. */
export interface ApprovalRow {
  subject_user_id: string | null;
  subject_org_id: string | null;
  subject_label: string | null;
  approved_lines: number;
  skipped_lines: number;
  approved_amount: string | number | null;
  /** One of `APPROVAL_OUTCOMES`, but treat an unknown value as informative. */
  outcome: string;
  /** The database's own sentence. Shown, never re-worded. */
  detail: string | null;
}

/**
 * A subject is identified by the PAIR, because either half may be null and a
 * commission subject is a person or an org, never both. Same key as
 * `commissionPayPeriods.subjectKey`, deliberately: the approve panel and the
 * period screen list the same people and must agree on who is who.
 */
export function subjectKey(row: {
  subject_user_id: string | null;
  subject_org_id: string | null;
}): string {
  return `${row.subject_user_id ?? '-'}:${row.subject_org_id ?? '-'}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// What is being approved
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ A UNION, NOT TWO FIELDS, BECAUSE THE DATABASE REFUSES THE FOURTH COMBINATION.
 *
 * `p_close_batch` defaults to "are we approving everyone?", and passing true
 * alongside a subject list raises 23514. Modelling scope as
 * `{ subjects, closeBatch }` would let a caller build that refusal and only
 * find out at the server.
 */
export type ApprovalScope =
  | { kind: 'whole-batch' }
  | { kind: 'named'; subjectUserIds: string[]; subjectOrgIds: string[] };

export function isWholeBatch(scope: ApprovalScope): boolean {
  return scope.kind === 'whole-batch';
}

/**
 * ⚠️ FINDING 1 IN A FUNCTION. Whether committing will change the PERIOD, as
 * opposed to changing any line.
 *
 * True for a whole-batch approve regardless of how many lines it touches --
 * including zero. This is what stops a confirm dialog reading an empty preview
 * as "nothing will happen".
 */
export function closesTheBatch(scope: ApprovalScope): boolean {
  return scope.kind === 'whole-batch';
}

/**
 * The arguments for the RPC, built in one place so the impossible pair cannot
 * be sent. `p_close_batch` is deliberately left out: its NULL default already
 * means "close if and only if this is the whole batch", which is exactly the
 * rule, and passing it explicitly would be this client restating a decision the
 * database already makes correctly.
 */
export function approvalArgs(
  periodId: string,
  scope: ApprovalScope,
  dryRun: boolean,
): {
  p_period_id: string;
  p_subject_user_ids: string[] | null;
  p_subject_org_ids: string[] | null;
  p_dry_run: boolean;
} {
  const named = scope.kind === 'named';
  // ⚠️ NULL, NOT []. An empty array is refused with 22023 ("approves nobody and
  // reports success"), which is the database refusing to let a client turn a
  // no-op into a success. Sending [] because a selection is empty would hit it.
  return {
    p_period_id: periodId,
    p_subject_user_ids: named && scope.subjectUserIds.length > 0 ? scope.subjectUserIds : null,
    p_subject_org_ids: named && scope.subjectOrgIds.length > 0 ? scope.subjectOrgIds : null,
    p_dry_run: dryRun,
  };
}

/** Whether a named scope actually names anybody. */
export function namesNobody(scope: ApprovalScope): boolean {
  return (
    scope.kind === 'named' &&
    scope.subjectUserIds.length === 0 &&
    scope.subjectOrgIds.length === 0
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary
// ─────────────────────────────────────────────────────────────────────────────

export interface ApprovalSummary {
  /** Rows that would approve at least one line. */
  approving: ApprovalRow[];
  /** Rows that would approve nothing, with the database's reason attached. */
  approvingNothing: ApprovalRow[];
  subjectCount: number;
  lineCount: number;
  skippedCount: number;
  /** null when a row carried an amount we could not read. Never coerced to 0. */
  total: Decimal | null;
  /** `would_approve` is a preview; `approved` is a commit that happened. */
  isPreview: boolean;
}

export function summariseApproval(rows: readonly ApprovalRow[]): ApprovalSummary {
  const approving = rows.filter((r) => (r.approved_lines ?? 0) > 0);
  const approvingNothing = rows.filter((r) => (r.approved_lines ?? 0) === 0);

  let total: Decimal | null = tryDecimal(0);
  for (const r of approving) {
    const amount = tryDecimal(r.approved_amount);
    // ⚠️ ONE UNREADABLE AMOUNT POISONS THE TOTAL, IT DOES NOT GET SKIPPED.
    // A total that quietly omits a row is worse than no total: it is a smaller
    // number presented as complete. Same rule as previewTotals().
    if (amount === null) {
      total = null;
      break;
    }
    total = total === null ? amount : addDecimal(total, amount);
  }

  return {
    approving,
    approvingNothing,
    subjectCount: rows.length,
    lineCount: approving.reduce((n, r) => n + (r.approved_lines ?? 0), 0),
    skippedCount: rows.reduce((n, r) => n + (r.skipped_lines ?? 0), 0),
    total,
    isPreview: rows.some((r) => r.outcome === 'would_approve'),
  };
}

/** Local add so this module does not re-export money arithmetic. */
function addDecimal(a: Decimal, b: Decimal): Decimal {
  const scale = Math.max(a.scale, b.scale);
  const lift = (d: Decimal) => d.units * BigInt(10) ** BigInt(scale - d.scale);
  return { units: lift(a) + lift(b), scale };
}

// ─────────────────────────────────────────────────────────────────────────────
// Copy
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ ASSERTED IN THE TEST, DELIBERATELY. This is the only place the product
 * explains what approving does and does not do, and every sentence here answers
 * a measured behaviour rather than a guess.
 */
export const APPROVAL_COPY = {
  /** Finding 2. */
  heldStayPending:
    'Held lines are not approved. They stay pending and cannot be paid until the hold clears.',
  /** Finding 1, and the most important sentence in this file. */
  closingAnEmptyBatch:
    'No lines can be approved, but approving still closes this period. Once closed it cannot be approved again, and a later correction lands in the next open period.',
  /** Finding 3. */
  namedLeavesOpen:
    'Approving named people leaves the period open, so the rest can still be approved.',
  wholeBatchCloses:
    'Approving everyone closes the period. It cannot be approved again afterwards.',
  /** The separation this epic exists to protect. */
  notPaying:
    'Approving is not paying. A separate authority marks an approved period paid, so that one person cannot move money end to end.',
  nobodySelected: 'Select at least one person, or approve everyone.',
} as const;

/**
 * One line for a confirmation screen. `isPreview` flips the tense.
 *
 * ⚠️ THE ZERO-LINE CASE IS NOT "NOTHING WILL HAPPEN" WHEN THE BATCH CLOSES.
 * That is finding 1, and it is the branch most likely to be lost in a tidy-up.
 */
export function describeApproval(summary: ApprovalSummary, scope: ApprovalScope): string {
  const verb = summary.isPreview ? 'will be approved' : 'approved';
  const money = summary.total === null ? null : formatMoneyDisplay(summary.total);

  if (summary.lineCount === 0) {
    return closesTheBatch(scope)
      ? APPROVAL_COPY.closingAnEmptyBatch
      : 'No lines can be approved for the people selected.';
  }

  const people = summary.approving.length;
  const head =
    `${summary.lineCount} ${summary.lineCount === 1 ? 'line' : 'lines'} ${verb}` +
    ` for ${people} ${people === 1 ? 'person' : 'people'}` +
    (money === null ? '' : `, totalling ${money}`) +
    '.';

  return summary.skippedCount > 0
    ? `${head} ${summary.skippedCount} ${summary.skippedCount === 1 ? 'line is' : 'lines are'} skipped. ${APPROVAL_COPY.heldStayPending}`
    : head;
}

// ─────────────────────────────────────────────────────────────────────────────
// The invalidation rule
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ A PREVIEW IS A STATEMENT ABOUT A MOMENT. Between the dry run and the
 * commit somebody else may have approved, held or reversed a line, and the
 * confirmation the operator read would no longer be honest. Compared
 * subject-by-subject rather than by count, because two sets of the same size
 * disagree about who is in them more often than about how many.
 *
 * Taken wholesale from `commissionBulkAssign.commitMatchesPreview()`. It is the
 * same hazard with different nouns, and having two spellings of one rule is how
 * one of them rots.
 */
export function commitMatchesPreview(
  preview: readonly ApprovalRow[],
  commit: readonly ApprovalRow[],
): { matches: true } | { matches: false; diverged: string[] } {
  const shape = (r: ApprovalRow) => `${r.approved_lines ?? 0}:${r.skipped_lines ?? 0}`;
  const before = new Map(preview.map((r) => [subjectKey(r), shape(r)]));
  const after = new Map(commit.map((r) => [subjectKey(r), shape(r)]));

  const diverged: string[] = [];
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    if (before.get(key) !== after.get(key)) diverged.push(key);
  }
  return diverged.length === 0 ? { matches: true } : { matches: false, diverged: diverged.sort() };
}

export const PREVIEW_DIVERGED =
  'What was approved is not what the preview showed, because the period changed while you were reading it. The approval went through as reported below.';
